import type { TFunction } from 'i18next';
import type { ChartSeries, PsychologicalResult, RadarDataLeadership } from '../../model';
import formatScoreOutOf from '../formatters';
import { ml360Height } from '.';
import { convertShortLabel } from '@/components/blocks/AssessmentResults/shared/utils';
import type { AssessmentResultItemDataFragment } from '@/gql/graphql';
import { ml360SeriesColors } from '@/pages/Reports/shared/chartColors';

const findMl360Result = (
    items: AssessmentResultItemDataFragment[],
    identifiers: readonly string[]
): Extract<AssessmentResultItemDataFragment['result'], { __typename: 'ML360Result' }> | undefined => {
    const item = items.find(
        i =>
            i.result.__typename === 'ML360Result' &&
            !!i.moduleInstance?.moduleDefinitionIdentifier &&
            identifiers.includes(i.moduleInstance.moduleDefinitionIdentifier)
    );
    return item?.result.__typename === 'ML360Result' ? item.result : undefined;
};

const findMl360ModuleInstance = (
    items: AssessmentResultItemDataFragment[],
    identifiers: readonly string[]
): AssessmentResultItemDataFragment['moduleInstance'] | undefined =>
    items.find(
        i =>
            i.result.__typename === 'ML360Result' &&
            !!i.moduleInstance?.moduleDefinitionIdentifier &&
            identifiers.includes(i.moduleInstance.moduleDefinitionIdentifier)
    )?.moduleInstance;

const parseMl360 = (t: TFunction, items: AssessmentResultItemDataFragment[]): PsychologicalResult | null => {
    const selfResult = findMl360Result(items, ['ml360-self', 'ml360-work-self']);
    const buddyResult = findMl360Result(items, ['ml360-buddy', 'ml360-work-buddy']);
    const trainerResult = findMl360Result(items, ['ml360-trainer', 'ml360-work-trainer']);

    const moduleInstance =
        findMl360ModuleInstance(items, ['ml360-self', 'ml360-work-self']) ??
        findMl360ModuleInstance(items, ['ml360-buddy', 'ml360-work-buddy']) ??
        findMl360ModuleInstance(items, ['ml360-trainer', 'ml360-work-trainer']);
    if (!moduleInstance?.moduleDefinitionIdentifier) {
        return null;
    }

    if (
        !selfResult?.categoryAverages?.length &&
        !buddyResult?.categoryAverages?.length &&
        !trainerResult?.categoryAverages?.length
    ) {
        return null;
    }

    const categories = Array.from(
        new Set<string>([
            ...(selfResult?.categoryAverages?.map(c => c.category) || []),
            ...(buddyResult?.categoryAverages?.map(c => c.category) || []),
            ...(trainerResult?.categoryAverages?.map(c => c.category) || []),
        ])
    );
    if (categories.length === 0) {
        return null;
    }

    const radarData: RadarDataLeadership[] = categories.map(category => {
        const selfScore = selfResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        const buddyScore = buddyResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        const trainerScore = trainerResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        return {
            axis: convertShortLabel(category),
            self: Number(selfScore.toFixed(1)),
            buddy: Number(buddyScore.toFixed(1)),
            trainer: Number(trainerScore.toFixed(1)),
        };
    });

    const allCategoryAverages = [
        ...(selfResult?.categoryAverages ?? []),
        ...(buddyResult?.categoryAverages ?? []),
        ...(trainerResult?.categoryAverages ?? []),
    ];
    const maxDomain = Math.max(1, ...(allCategoryAverages.length ? allCategoryAverages.map(c => c.maxScore) : [1]));

    const scoreCards =
        selfResult?.categoryAverages?.map(c => ({
            label: convertShortLabel(c.category),
            value: formatScoreOutOf(t, c.averageScore, c.maxScore, 1),
        })) || [];

    const series: ChartSeries[] = [
        {
            key: 'self',
            name: t('traineeReports:labels.self'),
            stroke: ml360SeriesColors.self.stroke,
            fill: ml360SeriesColors.self.fill,
            fillOpacity: ml360SeriesColors.self.fillOpacity,
        },
        {
            key: 'buddy',
            name: t('traineeReports:labels.buddy'),
            stroke: ml360SeriesColors.buddy.stroke,
            fill: ml360SeriesColors.buddy.fill,
            fillOpacity: ml360SeriesColors.buddy.fillOpacity,
        },
        {
            key: 'trainer',
            name: t('traineeReports:labels.trainer'),
            stroke: ml360SeriesColors.trainer.stroke,
            fill: ml360SeriesColors.trainer.fill,
            fillOpacity: ml360SeriesColors.trainer.fillOpacity,
        },
    ];

    return {
        moduleDefinitionIdentifier: moduleInstance.moduleDefinitionIdentifier ?? '',
        name: moduleInstance.name ?? t('traineeReports:ml360.title'),
        description: moduleInstance.description ?? t('traineeReports:ml360.description'),
        radarGroups: [
            {
                key: 'ml360',
                scoreCards,
                charts: [
                    {
                        data: radarData,
                        series,
                        maxDomain,
                        height: ml360Height,
                        variant: 'bare',
                        footer: t('traineeReports:labels.individual'),
                    },
                ],
            },
        ],
    };
};

export default parseMl360;
